// scenarios/presentation.ts — STRUCTURAL map of each scenario.
//
// Phase B i18n note: prior to Phase B this file ALSO held every user-facing
// English string (hero copy, "About" overlay sections, fine-print, etc.).
// Those strings have moved to web/src/locales/en/scenarios.json and are
// looked up at render time via `t('scenarios:<id>.<field>')`. This file
// continues to own STRUCTURAL fields only:
//   - `id`            — matches the server-side Scenario.id; identifier.
//   - `pillLabel`     — short brand-name label kept English-style; used as
//                       fallback alongside server-driven scenario.name and
//                       as the literal painted onto SVG book covers. Also
//                       lookable via t('scenarios:<id>.pillLabel') when a
//                       translated form is preferred.
//   - `genre`         — short genre tag; also lookable via
//                       t('scenarios:<id>.genre') for translation, kept
//                       here so legacy callers without i18n context still
//                       have something to render.
//   - `theme`         — colour + typography CSS custom-property values.
//   - `dimensionThemes` — per-dimension theme overlays (Veridian Bloom,
//                         The Sleeping Floor).
//
// Components that render this scenario's user-facing marketing copy must
// pull the translated strings via `useTranslation('scenarios')` and the
// matching key — never re-add hero/About text fields to this file.
//
// Keep `id` + `pillLabel` + `genre` in sync with server/src/scenarios/*.
// The hero/About translations live in locales/en/scenarios.json (with
// generated translations under locales/<lang>/scenarios.json).

/**
 * Theme — colour + typography variables for one scenario.
 *
 * Applied as CSS custom properties on the login surface root. Any login
 * style that would otherwise hard-code a colour reads from these variables.
 *
 * `fontFamily` uses system-font stacks only; we do NOT add web-font
 * dependencies. The five scenarios pick deliberately different stacks so
 * the tonal shift between them is felt, not just seen.
 */
export interface ScenarioTheme {
  /** Full background — typically a radial-gradient. */
  bgGradient: string;
  /** Primary accent (active pills, focus rings, primary CTAs). */
  accent: string;
  /** Softer accent for hover state, secondary CTAs, dividers. */
  accentMuted: string;
  /** Body text colour. */
  ink: string;
  /** Secondary text — labels, fine-print, dim copy. */
  inkMuted: string;
  /** Surface colour for cards and the marketing pane. */
  paper: string;
  /** Border / hairline colour on cards. */
  paperEdge: string;
  /** Body font stack — system fonts only. */
  fontFamily: string;
}

/**
 * ScenarioPresentation — the STRUCTURAL data the UI needs per scenario.
 *
 * No user-facing copy lives here post-Phase-B (other than the brand-name
 * `pillLabel` + short `genre` kept for fallbacks). All hero / About / fine-
 * print marketing text is keyed by scenario id under
 * `locales/<lang>/scenarios.json` and read via `t()`.
 *
 * NOTE: This is a web-side concern. The server-side `Scenario` type is
 * intentionally separate (see server/src/scenarios/types.ts).
 */
export interface ScenarioPresentation {
  /** Matches server-side `Scenario.id`. */
  id: string;
  /** Short brand-name label (e.g. "Ember Hollow"). Fallback for non-i18n callers. */
  pillLabel: string;
  /** Short genre label (e.g. "Gothic mystery", "Hard sci-fi"). ≤22 chars. */
  genre: string;
  /** The visual theme. */
  theme: ScenarioTheme;
  /**
   * Optional per-dimension theme overrides. Keyed by `state.dimension`
   * integer; each value is a partial theme that gets merged onto the base
   * `theme` when the player is on that dimension. Scenarios that don't
   * change theme with dimension (most of them — Vaultsworn included, even
   * though it uses `state.level` for spatial floors) leave this undefined.
   *
   * Only Veridian Bloom and The Sleeping Floor declare dimensionThemes
   * today (both have `layerSemantics: "dimensional"` server-side). The
   * wrapper falls back to the base `theme` when no entry exists for the
   * current dimension — partial overrides need not cover every dimension.
   *
   * See `ScenarioThemeWrapper.tsx` for the merge behaviour. Inline styles
   * only — no web fonts, no extra deps.
   */
  dimensionThemes?: Record<number, Partial<ScenarioTheme>>;
  /**
   * @deprecated Use `dimensionThemes`. Kept temporarily as a back-compat
   * alias during the rename; the wrapper consults it only when
   * `dimensionThemes` is absent. Will be removed once the migration is
   * complete.
   */
  layerThemes?: Record<number, Partial<ScenarioTheme>>;
}

// ── Themes ────────────────────────────────────────────────────────────────
// Reuse-of-shared-strings is left explicit per scenario so each entry can
// be tweaked independently without "themeing the themer". DO NOT factor
// these into a shared helper — the whole point is that each scenario's
// palette is a complete, hand-tuned thing.

const EMBER_HOLLOW_THEME: ScenarioTheme = {
  // Reused from the existing styles.ts loginShell so the default scenario
  // looks identical to the pre-multiscenario login.
  bgGradient:
    "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)",
  accent: "#d4a574",
  accentMuted: "rgba(212,165,116,0.45)",
  ink: "#e8d9b8",
  inkMuted: "#8a7355",
  paper: "rgba(20,14,10,0.55)",
  paperEdge: "rgba(212,165,116,0.18)",
  fontFamily:
    "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
};

const VERITY_DRIFT_THEME: ScenarioTheme = {
  // Cold-iron blue with halogen-white accents on near-black hull.
  bgGradient:
    "radial-gradient(ellipse at top, #0e161e 0%, #060a10 60%, #02040a 100%)",
  accent: "#a8c8e0",
  accentMuted: "rgba(168,200,224,0.45)",
  ink: "#dfe6ec",
  inkMuted: "#7d8a98",
  paper: "rgba(10,16,22,0.62)",
  paperEdge: "rgba(168,200,224,0.22)",
  // A clean humanist sans — the only sci-fi world among gothic siblings.
  // System-font stack only; no web-font dependency.
  fontFamily:
    "'Inter', 'Helvetica Neue', 'Segoe UI', system-ui, sans-serif",
};

const DROWNED_CATHEDRAL_THEME: ScenarioTheme = {
  // Muted teal / sea-glass on slate.
  bgGradient:
    "radial-gradient(ellipse at top, #102028 0%, #08141a 60%, #03080c 100%)",
  accent: "#6fb3b8",
  accentMuted: "rgba(111,179,184,0.45)",
  ink: "#d8e6e6",
  inkMuted: "#6f8e90",
  paper: "rgba(12,28,34,0.6)",
  paperEdge: "rgba(111,179,184,0.22)",
  fontFamily:
    "'Vollkorn', 'Iowan Old Style', 'Palatino Linotype', Palatino, 'URW Palladio L', Georgia, serif",
};

const HOLLOW_COURT_THEME: ScenarioTheme = {
  // Deep emerald / frost-silver on near-black.
  bgGradient:
    "radial-gradient(ellipse at top, #0d1a13 0%, #060d09 60%, #020403 100%)",
  accent: "#9ad6b1",
  accentMuted: "rgba(154,214,177,0.45)",
  ink: "#dfe6df",
  inkMuted: "#7d9483",
  paper: "rgba(10,18,14,0.6)",
  paperEdge: "rgba(154,214,177,0.22)",
  // A sharp transitional serif for courtly precision.
  fontFamily:
    "'Cormorant SC', 'Cormorant Garamond', 'Times New Roman', 'Liberation Serif', Times, serif",
};

const SALT_GLASS_TOWER_THEME: ScenarioTheme = {
  // Pale gold / bone-white with bronze accents.
  bgGradient:
    "radial-gradient(ellipse at top, #1c1810 0%, #110d07 60%, #060403 100%)",
  accent: "#d8b06a",
  accentMuted: "rgba(216,176,106,0.5)",
  ink: "#ece2cc",
  inkMuted: "#8c7a5a",
  paper: "rgba(24,20,12,0.6)",
  paperEdge: "rgba(216,176,106,0.22)",
  // An angular slab-leaning serif for desert geometry.
  fontFamily:
    "'Roboto Slab', 'Rockwell', 'Slabo 27px', 'Iowan Old Style', Georgia, serif",
};

const WATCHMANS_LANTERN_THEME: ScenarioTheme = {
  // Kerosene yellow on iron blue — the only sans-serif scenario, for tonal
  // contrast with the four serif worlds.
  bgGradient:
    "radial-gradient(ellipse at top, #0e1620 0%, #070c12 60%, #02040a 100%)",
  accent: "#f0c66c",
  accentMuted: "rgba(240,198,108,0.45)",
  ink: "#dde4ec",
  inkMuted: "#7c8898",
  paper: "rgba(12,18,28,0.6)",
  paperEdge: "rgba(240,198,108,0.22)",
  fontFamily:
    "'Inter', 'Helvetica Neue', 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
};

// Veridian Bloom — surface layer (layer 0): hyperreal, sterile, too-good.
// Layer-specific overrides are declared on the presentation below.
const VERIDIAN_BLOOM_THEME: ScenarioTheme = {
  bgGradient:
    "radial-gradient(ellipse at top, #eaf4ec 0%, #d4e6da 60%, #b5d0bf 100%)",
  accent: "#1f8a4a",
  accentMuted: "rgba(31,138,74,0.4)",
  ink: "#0e1a14",
  inkMuted: "#4a665a",
  paper: "rgba(255,255,255,0.78)",
  paperEdge: "rgba(31,138,74,0.22)",
  // Crisp humanist sans — the simulation reads as a clean modern app.
  fontFamily:
    "'Inter', 'Helvetica Neue', 'Segoe UI', system-ui, sans-serif",
};

const THE_GREENWAY_THEME: ScenarioTheme = {
  // Mossy forest-green and lamp-amber on a deep loam brown.
  bgGradient:
    "radial-gradient(ellipse at top, #1c2618 0%, #11180e 60%, #060904 100%)",
  accent: "#c79a4a",
  accentMuted: "rgba(199,154,74,0.45)",
  ink: "#e6dec5",
  inkMuted: "#8c7c5c",
  paper: "rgba(16,22,12,0.6)",
  paperEdge: "rgba(199,154,74,0.22)",
  // A long-line literary serif — Tolkien-shelf shape.
  fontFamily:
    "'Iowan Old Style', 'Constantia', 'Cormorant Garamond', 'Palatino Linotype', Palatino, Georgia, serif",
};

const VAULTSWORN_THEME: ScenarioTheme = {
  // Lamp-oil amber and tarnished bronze on stone-grey.
  bgGradient:
    "radial-gradient(ellipse at top, #1f1a14 0%, #14100b 60%, #07050a 100%)",
  accent: "#e3a85a",
  accentMuted: "rgba(227,168,90,0.5)",
  ink: "#ece0c4",
  inkMuted: "#8e7c5e",
  paper: "rgba(22,18,14,0.62)",
  paperEdge: "rgba(227,168,90,0.22)",
  // Slab-leaning serif — table-shape, character-sheet-shape.
  fontFamily:
    "'Roboto Slab', 'Rockwell', 'Slabo 27px', 'Iowan Old Style', Georgia, serif",
};

const THE_COLDWATER_LINE_THEME: ScenarioTheme = {
  // Smoke and cold-rain palette — gun-blue and brass with paper-cream ink.
  bgGradient:
    "radial-gradient(ellipse at top, #161c24 0%, #0c1118 60%, #050709 100%)",
  accent: "#c9a36b",
  accentMuted: "rgba(201,163,107,0.45)",
  ink: "#e6e2d5",
  inkMuted: "#8a8674",
  paper: "rgba(16,22,30,0.62)",
  paperEdge: "rgba(201,163,107,0.22)",
  // A clean transitional serif — period dossier, hotel-letterhead feel.
  fontFamily:
    "'EB Garamond', 'Cormorant Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
};

const SAUCER_COUNTRY_THEME: ScenarioTheme = {
  // Dusty desert-sunset palette — burnt orange and bunting-faded amber on
  // creosote-purple twilight. The chrome of a 1958 diner stool catches
  // the last of the light; the welcome banners are washed out.
  bgGradient:
    "radial-gradient(ellipse at top, #2a1a14 0%, #1a110b 60%, #08050a 100%)",
  accent: "#d4814a",
  accentMuted: "rgba(212,129,74,0.45)",
  ink: "#ecd9c0",
  inkMuted: "#8e7256",
  paper: "rgba(28,20,16,0.6)",
  paperEdge: "rgba(212,129,74,0.22)",
  // A mid-century slab serif — diner-signage, news-reel-title shape.
  fontFamily:
    "'Roboto Slab', 'Rockwell', 'Slabo 27px', 'Iowan Old Style', Georgia, serif",
};

// The Sleeping Floor — lobby layer (layer 0): warm amber, ordinary hotel.
// Layer-specific overrides are declared on the presentation below.
const THE_SLEEPING_FLOOR_THEME: ScenarioTheme = {
  bgGradient:
    "radial-gradient(ellipse at top, #2a1d12 0%, #1b1209 60%, #0a0604 100%)",
  accent: "#e8c180",
  accentMuted: "rgba(232,193,128,0.45)",
  ink: "#efe2c6",
  inkMuted: "#998363",
  paper: "rgba(28,20,12,0.6)",
  paperEdge: "rgba(232,193,128,0.22)",
  // A soft humanist serif — lobby-letterhead warmth.
  fontFamily:
    "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
};

// ── Presentations ─────────────────────────────────────────────────────────

const EMBER_HOLLOW: ScenarioPresentation = {
  id: "ember_hollow",
  pillLabel: "Ember Hollow",
  genre: "Gothic mystery",
  theme: EMBER_HOLLOW_THEME,
};

const VERITY_DRIFT: ScenarioPresentation = {
  id: "verity_drift",
  pillLabel: "Verity Drift",
  genre: "Hard sci-fi",
  theme: VERITY_DRIFT_THEME,
};

const DROWNED_CATHEDRAL: ScenarioPresentation = {
  id: "drowned_cathedral",
  pillLabel: "Drowned Cathedral",
  genre: "Liturgical horror",
  theme: DROWNED_CATHEDRAL_THEME,
};

const HOLLOW_COURT: ScenarioPresentation = {
  id: "hollow_court",
  pillLabel: "Hollow Court",
  genre: "Fae court",
  theme: HOLLOW_COURT_THEME,
};

const SALT_GLASS_TOWER: ScenarioPresentation = {
  id: "salt_glass_tower",
  pillLabel: "Salt-Glass Tower",
  genre: "Desert weird",
  theme: SALT_GLASS_TOWER_THEME,
};

const WATCHMANS_LANTERN: ScenarioPresentation = {
  id: "watchmans_lantern",
  pillLabel: "Watchman's Lantern",
  genre: "Ghost story",
  theme: WATCHMANS_LANTERN_THEME,
};

const VERIDIAN_BLOOM: ScenarioPresentation = {
  id: "veridian_bloom",
  pillLabel: "Veridian Bloom",
  genre: "Simulation thriller",
  theme: VERIDIAN_BLOOM_THEME,
  // Per-dimension overlays. The base theme (above) is dimension 0 — the
  // bright, sterile, hyperreal surface — and each entry below paints over
  // it as the player descends. Partial overrides; what isn't named falls
  // back to base. Veridian Bloom is `layerSemantics: "dimensional"`, so
  // crossings move state.dimension and trip these overlays.
  dimensionThemes: {
    // Dimension 1 — maintenance / glitch realm. Desaturated, broken, with
    // warning-yellow accents over a dim magenta wash.
    1: {
      bgGradient:
        "radial-gradient(ellipse at top, #2a1c2a 0%, #1a121e 60%, #0a0610 100%)",
      accent: "#f0c64a",
      accentMuted: "rgba(240,198,74,0.45)",
      ink: "#e6d8d0",
      inkMuted: "#a07a82",
      paper: "rgba(28,18,28,0.62)",
      paperEdge: "rgba(240,198,74,0.3)",
    },
    // Dimension 2 — control plane. Minimal grey-on-black, terminal monospace.
    2: {
      bgGradient:
        "radial-gradient(ellipse at top, #14181c 0%, #0a0c10 60%, #03050a 100%)",
      accent: "#c8d4d0",
      accentMuted: "rgba(200,212,208,0.4)",
      ink: "#dde2de",
      inkMuted: "#7d8884",
      paper: "rgba(12,16,20,0.7)",
      paperEdge: "rgba(200,212,208,0.18)",
      fontFamily:
        "ui-monospace, 'SF Mono', 'Menlo', 'Consolas', 'Liberation Mono', monospace",
    },
    // Dimension 3 — raw world / outside. Sodium-orange and rust on slate; weather.
    3: {
      bgGradient:
        "radial-gradient(ellipse at top, #2a1a10 0%, #1a1108 60%, #0a0604 100%)",
      accent: "#d8762e",
      accentMuted: "rgba(216,118,46,0.5)",
      ink: "#e8d8c4",
      inkMuted: "#8a6b54",
      paper: "rgba(28,18,12,0.66)",
      paperEdge: "rgba(216,118,46,0.3)",
      fontFamily:
        "'Iowan Old Style', 'Cormorant Garamond', 'Palatino Linotype', Palatino, Georgia, serif",
    },
  },
};

const THE_GREENWAY: ScenarioPresentation = {
  id: "the_greenway",
  pillLabel: "The Greenway",
  genre: "Epic fantasy",
  theme: THE_GREENWAY_THEME,
};

const VAULTSWORN: ScenarioPresentation = {
  id: "vaultsworn",
  pillLabel: "Vaultsworn",
  genre: "Dungeon delve",
  theme: VAULTSWORN_THEME,
};

const THE_COLDWATER_LINE: ScenarioPresentation = {
  id: "the_coldwater_line",
  pillLabel: "Coldwater Line",
  genre: "Spy thriller",
  theme: THE_COLDWATER_LINE_THEME,
};

const SAUCER_COUNTRY: ScenarioPresentation = {
  id: "saucer_country",
  pillLabel: "Saucer Country",
  genre: "Alien invasion",
  theme: SAUCER_COUNTRY_THEME,
};

const THE_SLEEPING_FLOOR: ScenarioPresentation = {
  id: "the_sleeping_floor",
  pillLabel: "Sleeping Floor",
  genre: "Dream architecture",
  theme: THE_SLEEPING_FLOOR_THEME,
  // Per-dimension overlays. Base theme above = dimension 0 (lobby, warm
  // amber). Each entry below paints the deeper dimension over the base.
  // The Sleeping Floor is `layerSemantics: "dimensional"`, so crossings
  // move state.dimension and trip these overlays.
  dimensionThemes: {
    // Dimension 1 — host's main dream. Saturated, dusk-blue, faintly tilted.
    1: {
      bgGradient:
        "radial-gradient(ellipse at top, #1a2a3e 0%, #0d1828 60%, #050a14 100%)",
      accent: "#7eaed8",
      accentMuted: "rgba(126,174,216,0.45)",
      ink: "#dde5ef",
      inkMuted: "#7c8da0",
      paper: "rgba(14,22,36,0.62)",
      paperEdge: "rgba(126,174,216,0.22)",
    },
    // Dimension 2 — buried memory. Sepia, fading.
    2: {
      bgGradient:
        "radial-gradient(ellipse at top, #2c2418 0%, #1a160e 60%, #0a0805 100%)",
      accent: "#caa86e",
      accentMuted: "rgba(202,168,110,0.4)",
      ink: "#e2d6b6",
      inkMuted: "#998864",
      paper: "rgba(28,22,14,0.62)",
      paperEdge: "rgba(202,168,110,0.2)",
      fontFamily:
        "'Iowan Old Style', 'Cormorant Garamond', 'Palatino Linotype', Palatino, Georgia, serif",
    },
    // Dimension 3 — limbo. Colourless, monochrome, static.
    3: {
      bgGradient:
        "radial-gradient(ellipse at top, #2a2a2a 0%, #1a1a1a 60%, #060606 100%)",
      accent: "#b8b8b8",
      accentMuted: "rgba(184,184,184,0.35)",
      ink: "#d8d8d8",
      inkMuted: "#787878",
      paper: "rgba(20,20,20,0.7)",
      paperEdge: "rgba(184,184,184,0.18)",
      fontFamily:
        "'Iowan Old Style', 'Cormorant Garamond', 'Palatino Linotype', Palatino, Georgia, serif",
    },
  },
};

// ── Public exports ────────────────────────────────────────────────────────

/**
 * Iteration order for the pill row + the catalogue. ember_hollow first
 * (it's the default + the only free-tier scenario), then in a curated
 * order that matches server/src/scenarios/index.ts.
 */
export const SCENARIO_PRESENTATION_ORDER: string[] = [
  "ember_hollow",
  "verity_drift",
  "drowned_cathedral",
  "hollow_court",
  "salt_glass_tower",
  "watchmans_lantern",
  "veridian_bloom",
  "the_greenway",
  "vaultsworn",
  "the_coldwater_line",
  "the_sleeping_floor",
  "saucer_country",
];

export const SCENARIO_PRESENTATIONS: Record<string, ScenarioPresentation> = {
  ember_hollow: EMBER_HOLLOW,
  verity_drift: VERITY_DRIFT,
  drowned_cathedral: DROWNED_CATHEDRAL,
  hollow_court: HOLLOW_COURT,
  salt_glass_tower: SALT_GLASS_TOWER,
  watchmans_lantern: WATCHMANS_LANTERN,
  veridian_bloom: VERIDIAN_BLOOM,
  the_greenway: THE_GREENWAY,
  vaultsworn: VAULTSWORN,
  the_coldwater_line: THE_COLDWATER_LINE,
  the_sleeping_floor: THE_SLEEPING_FLOOR,
  saucer_country: SAUCER_COUNTRY,
};

/**
 * Default scenario id — the one the login screen lands on before any pill
 * is selected. Must match the server's DEFAULT_SCENARIO_ID.
 */
export const DEFAULT_SCENARIO_ID = "ember_hollow";
